"use client";

import { useMemo, useState } from "react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";
import {
    DELAI_DEPOT_MOIS,
    DELAI_REPONSE_MOIS,
    PLAFOND_MODELE_SIMPLIFIE,
    PUBLICS,
    composerPresentation,
    concerne,
    etatDuDelai,
    recapitulatif,
    valider,
} from "@/lib/tools/trainingProviderDeclaration";
import { dateEnToutesLettres, documentEnTexte, nomDuFichier } from "@/lib/tools/documentText";
import { telechargerDocument } from "@/lib/tools/documentPdf";
import { ApercuDocument } from "./DocumentPaper";
import { PadSignature, useSignature } from "./SignaturePad";
import {
    Avancer,
    Champ,
    Choix,
    Ecran,
    Recommencer,
    Resultat,
    Segments,
    remplir,
    useParcours,
} from "./Journey";
import { useDossierOutil, useSignalerIdentite } from "./ToolsDossier";

/**
 * La déclaration d'activité de formation, et la pièce qui la conditionne.
 *
 * CE PARCOURS NE FABRIQUE PAS LA DÉCLARATION : elle se dépose sur le portail
 * « Mon activité formation », ou sur le Cerfa 10782*06, qui n'a aucun champ
 * remplissable. Il fabrique la présentation d'activité que l'arrêté du
 * 24 novembre 2025 impose au micro-entrepreneur, et que rien d'autre ne
 * produit.
 *
 * LE TRI PASSE AVANT LA SAISIE, comme dans les autres formulaires : sans
 * première convention signée il n'y a rien à déposer, au-dessus du plafond le
 * modèle simplifié ne s'applique pas, et une activité qui n'est pas de la
 * formation professionnelle ne se déclare pas du tout.
 */

const L = {
    fr: {
        phases: ["Votre situation", "Votre formation", "Signature"],
        retour: "Retour",
        prive: "Tout se fait sur votre appareil : le document s'y fabrique, personne ne reçoit vos réponses, pas même nous, et tout disparaît avec l'onglet.",

        natureTitre: "Vendez-vous des actions de formation professionnelle ?",
        natureAide: "Une formation a un objectif professionnel, un programme et une évaluation. Un accompagnement qui s'adapte à ce que vit la personne, sans programme fixé d'avance, n'en est pas une.",
        natureOui: "Oui, je forme",
        natureOuiDetail: "Un programme, un objectif professionnel, une évaluation",
        natureNon: "Non, j'accompagne ou je conseille",
        natureNonDetail: "Coaching, conseil, prestation sur mesure",

        contratTitre: "Avez-vous signé votre premier contrat ou votre première convention ?",
        contratAide: "Vous ne pouvez déclarer qu'après cette signature, qui ouvre aussi le délai de dépôt.",
        contratOui: "Oui, c'est signé",
        contratOuiDetail: "Avec un particulier, une entreprise ou un donneur d'ordre",
        contratNon: "Pas encore",
        contratNonDetail: "Je prépare mon activité",

        plafondTitre: "Votre chiffre d'affaires dépasse-t-il {plafond} ?",
        plafondAide: "Sous ce montant, vous avez droit à un dossier allégé et à un modèle de présentation d'activité en une page. Au-dessus, c'est le dossier complet qu'il faut constituer.",
        plafondNon: "Non, je suis en dessous",
        plafondNonDetail: "Le cas de la plupart des micro-entrepreneurs",
        plafondOui: "Oui, je dépasse",
        plafondOuiDetail: "Ou je ne suis pas au régime micro-social",

        horsSujetTitre: "Ce n'est pas ce document qu'il vous faut",
        refusHorsFormation: "La déclaration d'activité ne concerne que les actions de formation professionnelle, au sens du code du travail. Un accompagnement individuel, du conseil ou du coaching qui n'a ni programme ni objectif professionnel défini n'en est pas une, et n'a pas à être déclaré. Déclarer quand même vous engagerait à transmettre un bilan chaque année, et à vous en tenir aux règles des organismes de formation.",
        refusPasDeContrat: "Le délai de {mois} mois court à partir de la signature de votre premier contrat ou de votre première convention de formation, et pas avant. Tant que rien n'est signé, il n'y a rien à déclarer : votre déclaration serait sans objet, et le modèle de présentation d'activité vous demande justement la date de cette signature. Revenez le jour où vous aurez votre premier contrat, vous aurez trois mois devant vous.",
        refusPlafond: "Le modèle allégé est réservé aux micro-entrepreneurs dont le chiffre d'affaires ne dépasse pas {plafond}. Au-dessus, vous devez constituer le dossier complet : le programme de vos actions, la liste de vos formateurs avec leurs titres, et les pièces prévues par l'article R. 6351-5. La déclaration, elle, reste la même et se dépose au même endroit.",

        identiteTitre: "Qui déclare ?",
        identiteAide: "Le nom et le SIRET rattachent cette pièce à votre déclaration : elle circule seule dans le dossier.",
        champPrenoms: "Prénoms",
        champNom: "Nom",
        champSiret: "Numéro SIRET",
        aideSiret: "Quatorze chiffres, sur votre avis de situation ou votre notification d'immatriculation.",

        contratDetailTitre: "Votre premier contrat de formation",
        contratDetailAide: "C'est la rubrique A du modèle. Sa date fixe la date limite de dépôt de votre déclaration.",
        champDateSignature: "Date de signature",
        champTypeContrat: "Avec qui ?",
        typeParticulier: "Un particulier",
        typeEntreprise: "Une entreprise",
        typeSousTraitance: "Sous-traitance",
        champSirenEntreprise: "SIREN de l'entreprise",
        champSirenDonneur: "SIREN du donneur d'ordre",
        aideSiren: "Neuf chiffres. Le modèle le demande dès que le contrat n'est pas passé avec un particulier.",
        delaiOuvert: "Vous avez jusqu'au {limite} pour déposer votre déclaration.",
        delaiDepasse: "Le délai de {mois} mois est passé depuis le {limite}. Déposez votre déclaration sans attendre : vous restez tenu de la faire.",

        actionTitre: "L'action de formation, et qui vous formez",
        actionAide: "Rubriques C et D du modèle. Indiquez le nombre de personnes pour chaque type de public concerné, et laissez les autres vides.",
        champDateAction: "Date prévue de l'action",
        publicSalarie: "Salarié",
        publicAlternant: "Alternant, apprenti compris",
        publicIndependant: "Indépendant",
        publicDemandeurEmploi: "Demandeur d'emploi",
        publicAutres: "Autres",
        champAutresPrecision: "Précisez qui",
        aideAutresPrecision: "Le modèle demande de nommer ce public, par exemple « élus d'une association » ou « bénévoles ».",
        totalForme: "{total} personne formée.",
        totalFormes: "{total} personnes formées.",

        contenuTitre: "Ce que vous enseignez",
        contenuAide: "Rubriques E, F et G. C'est la partie que la DREETS lit vraiment : deux ou trois phrases précises valent mieux qu'une page.",
        champObjectif: "Objectif professionnel de la formation",
        aideObjectif: "Ce que les personnes formées sauront faire à la fin, et la certification visée s'il y en a une.",
        champFormateur: "Vos titres et votre expérience",
        aideFormateur: "Ce qui vous rend légitime sur ce sujet précis : diplômes, années de métier, certifications.",
        champMoyens: "Moyens techniques et pédagogiques",
        aideMoyens: "Ce que vous mettez entre les mains des personnes formées : support, livret, exercices, matériel.",

        modalitesTitre: "Comment se déroule la formation ?",
        modalitesAide: "Rubrique H. Dès qu'une partie se fait à distance, le modèle exige de décrire l'accompagnement.",
        champModalite: "Déroulement",
        modalitePresentiel: "En présentiel",
        modaliteDistanciel: "À distance",
        modaliteMixte: "Les deux",
        champAccompagnement: "Accompagnement à distance",
        aideAccompagnement: "Comment vous restez joignable et comment vous suivez la progression : visio, messagerie, corrections.",

        signatureTitre: "Où et quand signez-vous ?",
        signatureAide: "Le modèle ne prévoit pas de signature, mais cette pièce circule seule dans votre dossier : la dater et la signer évite qu'on vous la redemande.",
        champLieuSignature: "Lieu de signature",
        champDateDeSignature: "Date de signature",
        signatureDessin: "Votre signature",
        signatureDessinAide: "Facultative. Tracez-la à la souris ou au doigt, ou laissez l'emplacement vide pour signer à la main après impression. Elle sera reprise par vos autres documents de la visite.",
        signatureReprise: "Reprise de votre document précédent. Retracez-la par-dessus ou effacez-la si ce n'est pas la bonne.",
        effacer: "Effacer",

        continuer: "Continuer",
        voirDocument: "Voir mon document",
        resultatTitre: "Votre présentation d'activité",
        recapTitre: "Ce que vous déclarez",
        apercu: "Aperçu du document",
        telecharger: "Télécharger en PDF",
        copier: "Copier le texte",
        copie: "Texte copié",
        recommencer: "Recommencer",

        manquesTitre: "Il manque encore quelque chose",
        manquesAide: "Complétez les champs signalés pour obtenir votre document.",
        verifierTitre: "Une valeur à vérifier",
        verifierAide: "Ces champs sont remplis, mais il y a un problème dans ce qu'ils contiennent. Corrigez-les, puis revenez ici.",
        erreurFormat: "Ce format n'est pas reconnu.",
        erreurSiret: "Un SIRET compte quatorze chiffres. Le vôtre en a {compte}. Attention à ne pas donner votre SIREN, qui en compte neuf.",
        erreurSiren: "Un SIREN compte neuf chiffres. Celui que vous avez saisi en a {compte}.",
        erreurAVenir: "Cette date est dans le futur. Le délai ne court qu'à partir d'une signature déjà intervenue.",
        alerteRetard: "Le délai de dépôt est passé depuis le {limite}. Déposez votre déclaration dès que possible : elle reste obligatoire, et former sans être déclaré vous expose.",

        suiteTitre: "Et maintenant",
        suite1: "Déposez votre déclaration sur le portail « Mon activité formation », ou envoyez le Cerfa 10782 à la DREETS de votre région, et joignez-y ce document.",
        suite2: "Ajoutez les trois autres pièces : votre justificatif SIREN, le bulletin n° 3 de votre casier judiciaire et une copie de votre pièce d'identité.",
        suite3: "Gardez votre contrat de formation signé : vous n'avez pas à le joindre, mais vous devez pouvoir le produire.",
        suite4: "Comptez {mois} mois pour la réponse. Passé ce délai, le silence de l'administration vaut enregistrement.",

        fondTitre: "Ce qui vous attend chaque année",
        fond: "Une fois déclaré, vous devez transmettre un bilan pédagogique et financier chaque année, au printemps. Sans ce bilan, ou s'il ne montre aucune formation réalisée, votre déclaration devient caduque et il faut tout recommencer. Le numéro de déclaration d'activité n'est pas un agrément et ne vaut pas certification Qualiopi. Pour que vos clients obtiennent des financements publics ou mutualisés, il vous faut cette certification.",
    },
    en: {
        phases: ["Your situation", "Your training", "Signature"],
        retour: "Back",
        prive: "Everything happens on your device: the document is built there, nobody receives your answers, not even us, and it all disappears with the tab.",

        natureTitre: "Do you sell professional training?",
        natureAide: "Training has a professional objective, a programme and an assessment. Support that adapts to whatever the person is going through, with no programme set in advance, is not training.",
        natureOui: "Yes, I train people",
        natureOuiDetail: "A programme, a professional objective, an assessment",
        natureNon: "No, I coach or advise",
        natureNonDetail: "Coaching, consulting, bespoke work",

        contratTitre: "Have you signed your first training contract or agreement?",
        contratAide: "You can only declare once that signature exists, and it also starts the filing deadline.",
        contratOui: "Yes, it is signed",
        contratOuiDetail: "With an individual, a company or a main contractor",
        contratNon: "Not yet",
        contratNonDetail: "I am still setting up",

        plafondTitre: "Is your turnover above {plafond}?",
        plafondAide: "Below that figure you are entitled to a lighter file and to a one-page activity presentation. Above it, you have to put together the full file.",
        plafondNon: "No, I am below",
        plafondNonDetail: "Where most micro-entrepreneurs are",
        plafondOui: "Yes, I am above",
        plafondOuiDetail: "Or I am not under the micro-social scheme",

        horsSujetTitre: "This is not the document you need",
        refusHorsFormation: "The activity declaration only covers professional training, as the labour code defines it. One-to-one support, consulting or coaching with no programme and no defined professional objective is not training, and does not have to be declared. Declaring anyway would commit you to filing a report every year, and to following the rules that bind training providers.",
        refusPasDeContrat: "The {mois}-month deadline runs from the signature of your first training contract or agreement, and not before. Until something is signed there is nothing to declare: your declaration would serve no purpose, and the presentation model asks precisely for the date of that signature. Come back the day you have your first contract, and you will have three months ahead of you.",
        refusPlafond: "The lighter model is reserved for micro-entrepreneurs whose turnover does not exceed {plafond}. Above it, you have to put together the full file: the programme of your courses, the list of your trainers with their qualifications, and the documents set out in article R. 6351-5. The declaration itself is the same, and is filed in the same place.",

        identiteTitre: "Who is declaring?",
        identiteAide: "The name and SIRET tie this document to your declaration: it travels on its own inside the file.",
        champPrenoms: "Forenames",
        champNom: "Surname",
        champSiret: "SIRET number",
        aideSiret: "Fourteen digits, on your situation notice or your registration confirmation.",

        contratDetailTitre: "Your first training contract",
        contratDetailAide: "This is section A of the model. Its date sets the deadline for filing your declaration.",
        champDateSignature: "Date of signature",
        champTypeContrat: "With whom?",
        typeParticulier: "An individual",
        typeEntreprise: "A company",
        typeSousTraitance: "Subcontracting",
        champSirenEntreprise: "Company SIREN",
        champSirenDonneur: "Main contractor's SIREN",
        aideSiren: "Nine digits. The model asks for it as soon as the contract is not with an individual.",
        delaiOuvert: "You have until {limite} to file your declaration.",
        delaiDepasse: "The {mois}-month deadline passed on {limite}. File your declaration without waiting: you are still required to.",

        actionTitre: "The training itself, and who you are training",
        actionAide: "Sections C and D of the model. Enter the number of people for each type of audience concerned, and leave the others empty.",
        champDateAction: "Planned date of the course",
        publicSalarie: "Employee",
        publicAlternant: "Apprentice or work-study",
        publicIndependant: "Self-employed",
        publicDemandeurEmploi: "Jobseeker",
        publicAutres: "Others",
        champAutresPrecision: "Say who",
        aideAutresPrecision: "The model asks you to name that audience, for instance « association officers » or « volunteers ».",
        totalForme: "{total} person trained.",
        totalFormes: "{total} people trained.",

        contenuTitre: "What you teach",
        contenuAide: "Sections E, F and G. This is the part the inspectorate actually reads: two or three precise sentences beat a full page.",
        champObjectif: "Professional objective of the course",
        aideObjectif: "What the people trained will be able to do at the end, and the certification aimed at if there is one.",
        champFormateur: "Your qualifications and experience",
        aideFormateur: "What makes you legitimate on this particular subject: degrees, years in the trade, certifications.",
        champMoyens: "Technical and teaching resources",
        aideMoyens: "What you put in the hands of the people trained: handouts, workbook, exercises, equipment.",

        modalitesTitre: "How does the course run?",
        modalitesAide: "Section H. As soon as any part happens remotely, the model requires you to describe the support you provide.",
        champModalite: "Delivery",
        modalitePresentiel: "In person",
        modaliteDistanciel: "Remotely",
        modaliteMixte: "Both",
        champAccompagnement: "Remote support",
        aideAccompagnement: "How you stay reachable and how you follow progress: video calls, messaging, marked work.",

        signatureTitre: "Where and when are you signing?",
        signatureAide: "The model does not provide for a signature, but this document travels on its own inside your file: dating and signing it saves you being asked for it again.",
        champLieuSignature: "Place of signature",
        champDateDeSignature: "Date of signature",
        signatureDessin: "Your signature",
        signatureDessinAide: "Optional. Draw it with the mouse or your finger, or leave the space empty and sign by hand after printing. Your other documents in this visit will reuse it.",
        signatureReprise: "Taken from your previous document. Draw over it, or clear it if it is not the right one.",
        effacer: "Clear",

        continuer: "Continue",
        voirDocument: "See my document",
        resultatTitre: "Your activity presentation",
        recapTitre: "What you are declaring",
        apercu: "Document preview",
        telecharger: "Download as PDF",
        copier: "Copy the text",
        copie: "Text copied",
        recommencer: "Start again",

        manquesTitre: "Something is still missing",
        manquesAide: "Fill in the highlighted fields to get your document.",
        verifierTitre: "Something to check",
        verifierAide: "These fields are filled in, but there is a problem with what they contain. Correct them, then come back here.",
        erreurFormat: "This format is not recognised.",
        erreurSiret: "A SIRET has fourteen digits. Yours has {compte}. Take care not to give your SIREN, which has nine.",
        erreurSiren: "A SIREN has nine digits. The one you entered has {compte}.",
        erreurAVenir: "That date is in the future. The deadline only runs from a signature that has already happened.",
        alerteRetard: "The filing deadline passed on {limite}. File your declaration as soon as you can: it is still compulsory, and training without being declared leaves you exposed.",

        suiteTitre: "What happens next",
        suite1: "File your declaration on the « Mon activité formation » portal, or send form 10782 to your regional labour directorate, and attach this document.",
        suite2: "Add the three other documents: your SIREN certificate, extract n° 3 of your criminal record and a copy of your ID.",
        suite3: "Keep your signed training contract: you do not have to attach it, but you must be able to produce it.",
        suite4: "Allow {mois} months for the reply. After that, official silence counts as registration.",

        fondTitre: "What is expected of you every year",
        fond: "Once declared, you must file a teaching and financial report every year, in the spring. Without that report, or if it shows no training actually delivered, your declaration lapses and you have to start again. The declaration number is not an approval and is not the Qualiopi certification. For your clients to get public or pooled funding, you need that certification.",
    },
};

const LIBELLES_MANQUES = {
    fr: {
        nature: "La nature de votre activité",
        siret: "Votre numéro SIRET",
        prenoms: "Vos prénoms",
        nom: "Votre nom",
        dateSignature: "La date de signature du premier contrat",
        typeContrat: "Le type de contrat",
        sirenPartenaire: "Le SIREN de votre client ou donneur d'ordre",
        dateAction: "La date prévue de l'action de formation",
        publics: "Au moins un public formé, avec son nombre",
        autresPrecision: "La précision du public « Autres »",
        objectif: "L'objectif professionnel de la formation",
        formateur: "Vos titres et votre expérience",
        moyens: "Les moyens techniques et pédagogiques",
        modalite: "Le déroulement de la formation",
        accompagnement: "Les modalités d'accompagnement à distance",
        lieuDeSignature: "Le lieu de signature",
        dateDeSignature: "La date de signature",
    },
    en: {
        nature: "The nature of your activity",
        siret: "Your SIRET number",
        prenoms: "Your forenames",
        nom: "Your surname",
        dateSignature: "The date the first contract was signed",
        typeContrat: "The type of contract",
        sirenPartenaire: "Your client's or main contractor's SIREN",
        dateAction: "The planned date of the course",
        publics: "At least one audience, with its number",
        autresPrecision: "The detail of the « Others » audience",
        objectif: "The professional objective of the course",
        formateur: "Your qualifications and experience",
        moyens: "The technical and teaching resources",
        modalite: "How the course runs",
        accompagnement: "The remote support arrangements",
        lieuDeSignature: "The place of signature",
        dateDeSignature: "The date of signature",
    },
};

const PHASES_PAR_ECRAN = {
    nature: 0,
    contrat: 0,
    plafond: 0,
    horsSujet: 0,
    identite: 1,
    contratDetail: 1,
    action: 1,
    contenu: 1,
    modalites: 1,
    signature: 2,
    resultat: 2,
};

const VIDE = {
    nature: "",
    contratSigne: "",
    sousLePlafond: "",
    prenoms: "",
    nom: "",
    siret: "",
    dateSignature: "",
    typeContrat: "",
    sirenPartenaire: "",
    dateAction: "",
    public_salarie: "",
    public_alternant: "",
    public_independant: "",
    public_demandeurEmploi: "",
    public_autres: "",
    autresPrecision: "",
    objectif: "",
    formateur: "",
    moyens: "",
    modalite: "",
    accompagnement: "",
    lieuDeSignature: "",
    dateDeSignature: "",
};

export default function DeclarationActiviteFormation({ lang = "fr", aujourdhui }) {
    const t = L[lang] || L.fr;
    const { identite } = useDossierOutil();
    const [r, setR] = useState(() => ({ ...VIDE, ...identite }));
    const [copie, setCopie] = useState(false);
    const { signature, poser: poserSignature } = useSignature();

    const { etape, sens, reduit, rang, aller, retour, recommencer, communs } = useParcours(
        "nature",
        () => {
            setR({ ...VIDE, ...identite });
            setCopie(false);
        },
        PHASES_PAR_ECRAN,
        null,
        // Reprendre où l'on en était, le temps de l'onglet (Journey.jsx).
        { r, setR },
    );

    useSignalerIdentite(
        { prenoms: r.prenoms, nom: r.nom, siret: r.siret },
        ["contratDetail", "action", "contenu", "modalites", "signature", "resultat"].includes(etape),
    );

    const poser = (cle) => (valeur) => setR((p) => ({ ...p, [cle]: valeur }));

    const jour = String(aujourdhui).slice(0, 10);
    const reponses = useMemo(
        () => ({ ...r, dateDeSignature: r.dateDeSignature || jour }),
        [r, jour],
    );

    const controle = useMemo(() => valider(reponses, aujourdhui), [reponses, aujourdhui]);
    const declaration = useMemo(
        () => composerPresentation(reponses, aujourdhui),
        [reponses, aujourdhui],
    );
    const recap = useMemo(() => recapitulatif(reponses, aujourdhui), [reponses, aujourdhui]);
    const delai = useMemo(
        () => (r.dateSignature ? etatDuDelai(r.dateSignature, aujourdhui) : null),
        [r.dateSignature, aujourdhui],
    );

    const rempli = (...cles) => cles.every((c) => String(r[c] || "").trim());
    const chiffres = (cle) => String(r[cle] || "").replace(/\D/g, "").length;

    const refusDe = (champ) => (controle.invalides || []).find((i) => i.champ === champ);
    const erreurDe = (champ) => {
        const refus = refusDe(champ);
        if (!refus) return "";
        if (champ === "siret") return remplir(t.erreurSiret, { compte: chiffres("siret") });
        if (champ === "sirenPartenaire") {
            return remplir(t.erreurSiren, { compte: chiffres("sirenPartenaire") });
        }
        if (refus.motif === "aVenir") return t.erreurAVenir;
        return t.erreurFormat;
    };

    const commun = { ...communs, phases: t.phases, retour: t.retour, rang };
    const tri = concerne(r);
    const plafondLisible = `${PLAFOND_MODELE_SIMPLIFIE.toLocaleString(lang === "en" ? "en-GB" : "fr-FR")} €`;

    const publicsSaisis = PUBLICS.reduce((total, cle) => {
        const n = Number(String(r[`public_${cle}`] || "").trim());
        return total + (Number.isInteger(n) && n > 0 ? n : 0);
    }, 0);

    const telecharger = () =>
        telechargerDocument({
            document: declaration,
            signature,
            nomFichier: nomDuFichier("presentation-activite-formation", r),
        });

    const copier = async () => {
        if (!declaration) return;
        try {
            await navigator.clipboard.writeText(documentEnTexte(declaration));
            setCopie(true);
            window.setTimeout(() => setCopie(false), 2000);
        } catch {
            // Presse-papiers refusé : le PDF reste la voie normale.
        }
    };

    /* ---------------------------------------------------------------- */
    /* Le tri                                                            */
    /* ---------------------------------------------------------------- */

    if (etape === "nature") {
        return (
            <Ecran key={etape} {...commun} phase={0} titre={t.natureTitre} aide={t.natureAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="diploma-linear"
                        titre={t.natureOui}
                        detail={t.natureOuiDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, nature: "formationPro" }));
                            aller("contrat");
                        }}
                    />
                    <Choix
                        icone="chat-round-line-linear"
                        titre={t.natureNon}
                        detail={t.natureNonDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, nature: "horsFormation" }));
                            aller("horsSujet");
                        }}
                    />
                </div>
                <p className="text-xs text-gray-500 dark:text-lightBlue/70 mt-5 mb-0">{t.prive}</p>
            </Ecran>
        );
    }

    if (etape === "contrat") {
        return (
            <Ecran key={etape} {...commun} phase={0} onRetour={retour} titre={t.contratTitre} aide={t.contratAide}>
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="check-circle-linear"
                        titre={t.contratOui}
                        detail={t.contratOuiDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, contratSigne: "oui" }));
                            aller("plafond");
                        }}
                    />
                    <Choix
                        icone="clock-circle-linear"
                        titre={t.contratNon}
                        detail={t.contratNonDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, contratSigne: "pasEncore" }));
                            aller("horsSujet");
                        }}
                    />
                </div>
            </Ecran>
        );
    }

    if (etape === "plafond") {
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={0}
                onRetour={retour}
                titre={remplir(t.plafondTitre, { plafond: plafondLisible })}
                aide={t.plafondAide}
            >
                <div className="grid sm:grid-cols-2 gap-3">
                    <Choix
                        icone="check-circle-linear"
                        titre={t.plafondNon}
                        detail={t.plafondNonDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, sousLePlafond: "oui" }));
                            aller("identite");
                        }}
                    />
                    <Choix
                        icone="close-circle-linear"
                        titre={t.plafondOui}
                        detail={t.plafondOuiDetail}
                        onClick={() => {
                            setR((p) => ({ ...p, sousLePlafond: "non" }));
                            aller("horsSujet");
                        }}
                    />
                </div>
            </Ecran>
        );
    }

    if (etape === "horsSujet") {
        return (
            <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.horsSujetTitre} phases={t.phases} sens={sens} reduit={reduit}>
                <div className="mt-4">
                    <p className="text-[15px] leading-7 text-gray-600 dark:text-lightBlue mb-0">
                        {tri.motif === "horsFormation" && t.refusHorsFormation}
                        {tri.motif === "pasDeContrat" &&
                            remplir(t.refusPasDeContrat, { mois: DELAI_DEPOT_MOIS })}
                        {tri.motif === "auDessusDuPlafond" &&
                            remplir(t.refusPlafond, { plafond: plafondLisible })}
                    </p>
                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            </Resultat>
        );
    }

    /* ---------------------------------------------------------------- */
    /* La saisie                                                         */
    /* ---------------------------------------------------------------- */

    if (etape === "identite") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.identiteTitre} aide={t.identiteAide}>
                <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Champ libelle={t.champPrenoms} valeur={r.prenoms} poser={poser("prenoms")} />
                        <Champ libelle={t.champNom} valeur={r.nom} poser={poser("nom")} />
                    </div>
                    <Champ
                        libelle={t.champSiret}
                        aide={t.aideSiret}
                        valeur={r.siret}
                        poser={poser("siret")}
                        erreur={erreurDe("siret")}
                        inputMode="numeric"
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("prenoms", "nom", "siret") || Boolean(refusDe("siret"))}
                    onClick={() => aller("contratDetail")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "contratDetail") {
        const avecSiren = r.typeContrat === "entreprise" || r.typeContrat === "sousTraitance";
        return (
            <Ecran
                key={etape}
                {...commun}
                phase={1}
                onRetour={retour}
                titre={t.contratDetailTitre}
                aide={t.contratDetailAide}
            >
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateSignature}
                        valeur={r.dateSignature}
                        poser={poser("dateSignature")}
                        erreur={erreurDe("dateSignature")}
                    />
                    <div>
                        <Segments
                            libelle={t.champTypeContrat}
                            options={[
                                { cle: "particulier", libelle: t.typeParticulier },
                                { cle: "entreprise", libelle: t.typeEntreprise },
                                { cle: "sousTraitance", libelle: t.typeSousTraitance },
                            ]}
                            valeur={r.typeContrat}
                            poser={poser("typeContrat")}
                        />
                    </div>
                    {avecSiren && (
                        <Champ
                            libelle={
                                r.typeContrat === "entreprise" ? t.champSirenEntreprise : t.champSirenDonneur
                            }
                            aide={t.aideSiren}
                            valeur={r.sirenPartenaire}
                            poser={poser("sirenPartenaire")}
                            erreur={erreurDe("sirenPartenaire")}
                            inputMode="numeric"
                        />
                    )}
                    {delai && !delai.aVenir && (
                        <p
                            className={`text-sm leading-7 mb-0 ${
                                delai.depasse
                                    ? "text-amber-600 dark:text-amber-400"
                                    : "text-gray-600 dark:text-lightBlue"
                            }`}
                        >
                            {delai.depasse
                                ? remplir(t.delaiDepasse, {
                                      mois: DELAI_DEPOT_MOIS,
                                      limite: dateEnToutesLettres(delai.limite),
                                  })
                                : remplir(t.delaiOuvert, { limite: dateEnToutesLettres(delai.limite) })}
                        </p>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("dateSignature", "typeContrat") ||
                        Boolean(refusDe("dateSignature")) ||
                        (avecSiren && (!rempli("sirenPartenaire") || Boolean(refusDe("sirenPartenaire"))))
                    }
                    onClick={() => aller("action")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "action") {
        const lignes = [
            { cle: "salarie", libelle: t.publicSalarie },
            { cle: "alternant", libelle: t.publicAlternant },
            { cle: "independant", libelle: t.publicIndependant },
            { cle: "demandeurEmploi", libelle: t.publicDemandeurEmploi },
            { cle: "autres", libelle: t.publicAutres },
        ];
        const autresRempli = Number(String(r.public_autres || "").trim()) > 0;
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.actionTitre} aide={t.actionAide}>
                <div className="space-y-5">
                    <Champ
                        type="date"
                        libelle={t.champDateAction}
                        valeur={r.dateAction}
                        poser={poser("dateAction")}
                        erreur={erreurDe("dateAction")}
                    />
                    <div className="grid sm:grid-cols-2 gap-4">
                        {lignes.map((ligne) => (
                            <Champ
                                key={ligne.cle}
                                libelle={ligne.libelle}
                                valeur={r[`public_${ligne.cle}`]}
                                poser={poser(`public_${ligne.cle}`)}
                                inputMode="numeric"
                                placeholder="0"
                            />
                        ))}
                    </div>
                    {autresRempli && (
                        <Champ
                            libelle={t.champAutresPrecision}
                            aide={t.aideAutresPrecision}
                            valeur={r.autresPrecision}
                            poser={poser("autresPrecision")}
                        />
                    )}
                    {publicsSaisis > 0 && (
                        <p className="text-sm text-gray-600 dark:text-lightBlue mb-0">
                            {remplir(publicsSaisis > 1 ? t.totalFormes : t.totalForme, {
                                total: publicsSaisis,
                            })}
                        </p>
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={
                        !rempli("dateAction") ||
                        Boolean(refusDe("dateAction")) ||
                        publicsSaisis === 0 ||
                        (autresRempli && !rempli("autresPrecision"))
                    }
                    onClick={() => aller("contenu")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "contenu") {
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.contenuTitre} aide={t.contenuAide}>
                <div className="space-y-5">
                    <Champ
                        type="textarea"
                        libelle={t.champObjectif}
                        aide={t.aideObjectif}
                        valeur={r.objectif}
                        poser={poser("objectif")}
                    />
                    <Champ
                        type="textarea"
                        libelle={t.champFormateur}
                        aide={t.aideFormateur}
                        valeur={r.formateur}
                        poser={poser("formateur")}
                    />
                    <Champ
                        type="textarea"
                        libelle={t.champMoyens}
                        aide={t.aideMoyens}
                        valeur={r.moyens}
                        poser={poser("moyens")}
                    />
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!rempli("objectif", "formateur", "moyens")}
                    onClick={() => aller("modalites")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "modalites") {
        const distance = r.modalite === "distanciel" || r.modalite === "mixte";
        return (
            <Ecran key={etape} {...commun} phase={1} onRetour={retour} titre={t.modalitesTitre} aide={t.modalitesAide}>
                <div className="space-y-5">
                    <div>
                        <Segments
                            libelle={t.champModalite}
                            options={[
                                { cle: "presentiel", libelle: t.modalitePresentiel },
                                { cle: "distanciel", libelle: t.modaliteDistanciel },
                                { cle: "mixte", libelle: t.modaliteMixte },
                            ]}
                            valeur={r.modalite}
                            poser={poser("modalite")}
                        />
                    </div>
                    {distance && (
                        <Champ
                            type="textarea"
                            libelle={t.champAccompagnement}
                            aide={t.aideAccompagnement}
                            valeur={r.accompagnement}
                            poser={poser("accompagnement")}
                        />
                    )}
                </div>
                <Avancer
                    pleineLargeur
                    disabled={!r.modalite || (distance && !rempli("accompagnement"))}
                    onClick={() => aller("signature")}
                >
                    {t.continuer}
                </Avancer>
            </Ecran>
        );
    }

    if (etape === "signature") {
        return (
            <Ecran key={etape} {...commun} phase={2} onRetour={retour} titre={t.signatureTitre} aide={t.signatureAide}>
                <div className="space-y-5">
                    <Champ
                        libelle={t.champLieuSignature}
                        valeur={r.lieuDeSignature}
                        poser={poser("lieuDeSignature")}
                    />
                    <Champ
                        type="date"
                        libelle={t.champDateDeSignature}
                        valeur={r.dateDeSignature || jour}
                        poser={poser("dateDeSignature")}
                        erreur={erreurDe("dateDeSignature")}
                    />
                    <PadSignature
                        libelle={t.signatureDessin}
                        aide={t.signatureDessinAide}
                        effacer={t.effacer}
                        repriseLibelle={t.signatureReprise}
                        valeur={signature}
                        poser={poserSignature}
                    />
                </div>
                <Avancer pleineLargeur disabled={!rempli("lieuDeSignature")} onClick={() => aller("resultat")}>
                    {t.voirDocument}
                </Avancer>
            </Ecran>
        );
    }

    /* ---------------------------------------------------------------- */
    /* Le résultat                                                       */
    /* ---------------------------------------------------------------- */

    return (
        <Resultat onRetour={retour} retour={t.retour} key={etape} titre={t.resultatTitre} phases={t.phases} sens={sens} reduit={reduit}>
            {!declaration ? (
                <div className="mt-4">
                    <div className="font-bold text-lg dark:text-white leading-snug">
                        {controle.manques.length ? t.manquesTitre : t.verifierTitre}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-lightBlue mt-2 mb-0">
                        {controle.manques.length ? t.manquesAide : t.verifierAide}
                    </p>
                    <ul className="mt-4 space-y-2">
                        {controle.manques.map((cle) => (
                            <li key={cle} className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue">
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-red-500/70" />
                                <span>{LIBELLES_MANQUES[lang]?.[cle] || LIBELLES_MANQUES.fr[cle]}</span>
                            </li>
                        ))}
                        {(controle.invalides || []).map(({ champ, motif }) => (
                            <li
                                key={`${champ}-${motif}`}
                                className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                            >
                                <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500/70" />
                                <span>
                                    {LIBELLES_MANQUES[lang]?.[champ] || LIBELLES_MANQUES.fr[champ]}
                                    {" : "}
                                    {erreurDe(champ)}
                                </span>
                            </li>
                        ))}
                    </ul>
                    <Avancer pleineLargeur onClick={retour}>
                        {t.retour}
                    </Avancer>
                </div>
            ) : (
                <div className="mt-4">
                    {controle.alertes.includes("delaiDepasse") && delai && (
                        <div className="rounded-xl border border-amber-500/30 bg-amber-500/[0.06] p-4 mb-5">
                            <p className="text-sm text-gray-700 dark:text-lightBlue mb-0">
                                {remplir(t.alerteRetard, { limite: dateEnToutesLettres(delai.limite) })}
                            </p>
                        </div>
                    )}

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.recapTitre}
                    </div>
                    <div className="rounded-2xl border border-gray-200/70 dark:border-white/10 overflow-hidden mb-6">
                        <table className="w-full text-sm">
                            <tbody>
                                {recap.map(([titre, valeur]) => (
                                    <tr
                                        key={titre}
                                        className="border-b last:border-b-0 border-gray-200/70 dark:border-white/10"
                                    >
                                        <td className="px-5 py-3 text-gray-500 dark:text-lightBlue/70 align-top w-2/5">
                                            {titre}
                                        </td>
                                        <td className="px-5 py-3 font-medium dark:text-white">{valeur}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="text-[11px] font-semibold text-gray-500 dark:text-lightBlue/70 uppercase tracking-wider mb-3">
                        {t.apercu}
                    </div>
                    <ApercuDocument document={declaration} signature={signature} />

                    <div className="mt-5 flex flex-col sm:flex-row gap-3">
                        <button
                            type="button"
                            onClick={telecharger}
                            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[#D04515] text-white font-semibold transition-opacity duration-200 hover:opacity-90"
                        >
                            <SolarIcon name="download-minimalistic-linear" className="text-lg" />
                            {t.telecharger}
                        </button>
                        <button
                            type="button"
                            onClick={copier}
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border border-gray-200/70 dark:border-white/10 text-gray-600 dark:text-lightBlue font-semibold transition-colors duration-200 hover:border-primary/40 hover:text-primary"
                        >
                            <SolarIcon name={copie ? "check-circle-linear" : "copy-linear"} className="text-lg" />
                            {copie ? t.copie : t.copier}
                        </button>
                    </div>

                    <div className="mt-8">
                        <div className="flex items-center gap-2.5 mb-4">
                            <SolarIcon
                                name="clipboard-check-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-bold dark:text-white leading-snug">{t.suiteTitre}</div>
                        </div>
                        <ul className="space-y-3">
                            {[t.suite1, t.suite2, t.suite3, remplir(t.suite4, { mois: DELAI_REPONSE_MOIS })].map(
                                (ligne) => (
                                    <li
                                        key={ligne}
                                        className="flex gap-3 text-[15px] leading-7 text-gray-600 dark:text-lightBlue"
                                    >
                                        <span className="mt-[11px] h-1.5 w-1.5 shrink-0 rounded-full bg-gray-300 dark:bg-white/20" />
                                        <span>{ligne}</span>
                                    </li>
                                ),
                            )}
                        </ul>
                    </div>

                    <div className="mt-8 rounded-2xl border border-gray-200/70 dark:border-white/10 bg-white/60 dark:bg-white/[0.03] p-5">
                        <div className="flex items-center gap-2.5 mb-3">
                            <SolarIcon
                                name="calendar-linear"
                                className="text-xl shrink-0 text-gray-400 dark:text-lightBlue/60"
                            />
                            <div className="font-semibold dark:text-white leading-snug">{t.fondTitre}</div>
                        </div>
                        <p className="text-sm leading-7 text-gray-600 dark:text-lightBlue mb-0">{t.fond}</p>
                    </div>

                    <Recommencer onClick={recommencer} libelle={t.recommencer} />
                </div>
            )}
        </Resultat>
    );
}
